import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import { collectAllPages } from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { DEFAULT_AUDIT_SORT } from "./audit.types";
import type {
  AuditRecord,
  AuditRecordsPage,
  AuditSortField,
  AuditSortOrder,
  GetAuditRecordParams,
  ListAuditRecordsParams,
} from "./audit.types";

export const AUDIT_PATHS = {
  LIST: "/api/plataforma/auditoria",
} as const;

/** Recurso do detalhe de um registro; conforme o item 834, apenas GET. */
export function buildAuditRecordPath(id: string): string {
  return `${AUDIT_PATHS.LIST}/${encodeURIComponent(id)}`;
}

/** Ordenação padrão da consulta: por data/hora, do mais recente para o mais antigo. */
export const DEFAULT_AUDIT_ORDER: AuditSortOrder = DEFAULT_AUDIT_SORT.direction;
export const DEFAULT_AUDIT_SORT_FIELD: AuditSortField = DEFAULT_AUDIT_SORT.field;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readFirstText(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const text = readText(raw[key]).trim();
    if (text) return text;
  }

  return "";
}

function readCount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
}

function readFirstCount(raw: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const count = readCount(raw[key]);
    if (count !== null) return count;
  }

  return null;
}

function extractItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  for (const key of ["data", "items", "content", "registros", "auditoria"]) {
    const nested = payload[key];
    if (nested !== undefined) return extractItems(nested);
  }

  return [];
}

/**
 * Registro único do detalhamento. Só desce pelo envelope quando o campo é um
 * objeto ou uma lista: o próprio registro pode trazer a data em `data`.
 */
function extractDetail(payload: unknown): unknown {
  if (Array.isArray(payload)) return payload[0];
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "item", "content", "registro", "auditoria"]) {
    const nested = payload[key];
    if (isRecord(nested) || Array.isArray(nested)) return extractDetail(nested);
  }

  return payload;
}

/**
 * O filtro de Ano vira o período do primeiro ao último dia do ano escolhido:
 * a consulta recorta por data, e não por um campo de ano. As duas pontas vão em
 * ISO 8601 (UTC), do primeiro instante do dia 1º ao último instante de 31/12,
 * para que o ano inteiro caiba no intervalo.
 */
export function buildAuditYearRange(ano: number): { dataInicio: string; dataFim: string } {
  return {
    dataInicio: new Date(Date.UTC(ano, 0, 1, 0, 0, 0, 0)).toISOString(),
    dataFim: new Date(Date.UTC(ano, 11, 31, 23, 59, 59, 999)).toISOString(),
  };
}

function toAuditRecord(raw: unknown, index: number): AuditRecord | null {
  if (!isRecord(raw)) return null;

  const dataHora = readFirstText(raw, "dataHora", "data_hora", "createdAt", "created_at", "data");
  const acao = readFirstText(raw, "acao", "action", "alteracao");
  const modulo = readFirstText(raw, "modulo", "module");
  const responsavel = readFirstText(raw, "responsavel", "responsible", "usuario", "createdBy");
  if (!dataHora && !acao && !modulo && !responsavel) return null;

  const registroAlterado = isRecord(raw.registroAlterado) ? raw.registroAlterado : {};

  return {
    // O índice mantém a linha endereçável (chave da tabela, ação Detalhar) sem id.
    id: readFirstText(raw, "id", "auditoriaId") || `${dataHora}-${index}`,
    dataHora,
    acao,
    valorAnterior: readFirstText(raw, "valorAnterior", "valor_anterior", "previousValue"),
    valorNovo: readFirstText(raw, "valorNovo", "valor_novo", "newValue"),
    justificativa: readFirstText(raw, "justificativa", "justification"),
    modulo,
    responsavel,
    responsavelDetalhe: readFirstText(
      raw,
      "responsavelPerfil",
      "responsavel_perfil",
      "perfil",
      "cargo",
      "area",
      "setor",
    ),
    responsavelEmail: readFirstText(
      raw,
      "responsavelEmail",
      "responsavel_email",
      "email",
      "usuarioEmail",
    ),
    responsavelTelefone: readFirstText(
      raw,
      "responsavelTelefone",
      "responsavel_telefone",
      "telefone",
      "phone",
      "celular",
    ),
    registroAlteradoTipo: readFirstText(registroAlterado, "tipo"),
    registroAlteradoNome: readFirstText(registroAlterado, "nome"),
    registroAlteradoEmail: readFirstText(registroAlterado, "email"),
    registroAlteradoCodigo: readFirstText(registroAlterado, "codigo"),
  };
}

function extractTotal(payload: unknown): number | null {
  if (!isRecord(payload)) return null;

  const meta = isRecord(payload.meta) ? payload.meta : undefined;
  const pagination = isRecord(meta?.pagination)
    ? meta.pagination
    : isRecord(payload.pagination)
      ? payload.pagination
      : undefined;

  for (const source of [pagination, meta, payload]) {
    if (!source) continue;

    const count = readFirstCount(source, "total", "totalRecords", "totalElements", "totalItens");
    if (count !== null) return count;
  }

  return isRecord(payload.data) ? extractTotal(payload.data) : null;
}

function toTimestamp(value: string): number | null {
  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? null : timestamp;
}

/** Comparação de texto no idioma da tela: sem distinguir acento nem caixa. */
const TEXT_COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/** Valor comparável da coluna ordenada; `null` quando o registro não tem o dado. */
function sortValue(record: AuditRecord, field: AuditSortField): number | string | null {
  if (field === "dataHora") return toTimestamp(record.dataHora);

  const text = record[field].trim();
  return text === "" ? null : text;
}

/**
 * Reforça a ordenação pedida sobre a página devolvida, para que a listagem em
 * tela nunca contrarie o cabeçalho acionado. Registros sem o dado da coluna
 * ficam no fim nas duas direções.
 */
function sortRecords(
  records: AuditRecord[],
  ordenarPor: AuditSortField,
  ordem: AuditSortOrder,
): AuditRecord[] {
  const direction = ordem === "asc" ? 1 : -1;

  return [...records].sort((a, b) => {
    const first = sortValue(a, ordenarPor);
    const second = sortValue(b, ordenarPor);
    if (first === null && second === null) return 0;
    if (first === null) return 1;
    if (second === null) return -1;

    const comparison =
      typeof first === "number" && typeof second === "number"
        ? first - second
        : TEXT_COLLATOR.compare(String(first), String(second));

    return comparison * direction;
  });
}

/**
 * Consulta somente leitura dos registros de auditoria das Obras.
 *
 * Contrato acordado com o back-end (tarefa 1046): filtros combinados em conjunto
 * (`status`, o período `dataInicio`/`dataFim` do ano escolhido, os códigos de
 * `acao` (a Alteração da tela) e `modulo` e o `responsavelId`), paginação em
 * `pagina`/`tamanhoPagina` e ordenação em `ordenarPor`/`ordem`. A coluna e a
 * direção vão na consulta para que a ordenação valha sobre o conjunto total de
 * registros filtrados, e não apenas sobre a página em tela.
 * O recorte às Obras (RN001) é aplicado pelo próprio serviço.
 */
export async function listAuditRecords({
  status,
  ano,
  alteracao,
  modulo,
  responsavelId,
  pagina,
  tamanhoPagina,
  ordenarPor = DEFAULT_AUDIT_SORT_FIELD,
  ordem = DEFAULT_AUDIT_ORDER,
  signal,
}: ListAuditRecordsParams = {}): Promise<AuditRecordsPage> {
  const params: Record<string, string | number> = {};
  if (status) params.status = status;
  if (ano) Object.assign(params, buildAuditYearRange(ano));
  // O filtro chama-se Alteração na tela, mas o serviço lê o código como `acao`.
  if (alteracao) params.acao = alteracao;
  if (modulo) params.modulo = modulo;
  if (responsavelId?.trim()) params.responsavel = responsavelId.trim();
  if (pagina) params.pagina = pagina;
  if (tamanhoPagina) params.tamanhoPagina = tamanhoPagina;
  params.ordenarPor = ordenarPor;
  params.ordem = ordem;

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(AUDIT_PATHS.LIST, config);

  const records = sortRecords(
    extractItems(response.data)
      .map(toAuditRecord)
      .filter((record): record is AuditRecord => record !== null),
    ordenarPor,
    ordem,
  );

  return { records, total: extractTotal(response.data) ?? records.length };
}

/**
 * Conjunto completo do resultado da consulta em vigor, percorrido página a
 * página e reordenado ao fim para que a sequência valha sobre todas as páginas.
 */
export async function listAllAuditRecords({
  ordenarPor = DEFAULT_AUDIT_SORT_FIELD,
  ordem = DEFAULT_AUDIT_ORDER,
  ...params
}: Omit<ListAuditRecordsParams, "pagina" | "tamanhoPagina"> = {}): Promise<AuditRecord[]> {
  const records = await collectAllPages((pagina, tamanhoPagina) =>
    listAuditRecords({ ...params, ordenarPor, ordem, pagina, tamanhoPagina }).then(
      ({ records: items, total }) => ({ items, total }),
    ),
  );

  return sortRecords(records, ordenarPor, ordem);
}

/**
 * Detalhe somente leitura do registro acionado em Detalhar.
 *
 * Contrato do item 834: `GET /api/plataforma/auditoria/{id}`, sem nenhuma
 * escrita (RN004/RN005). Devolve `null` quando a resposta chega sem o registro
 * — a tela trata esse caso como registro não encontrado.
 */
export async function getAuditRecord({
  id,
  signal,
}: GetAuditRecordParams): Promise<AuditRecord | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildAuditRecordPath(id), config);
  const record = toAuditRecord(extractDetail(response.data), 0);

  // RN001 — o detalhe pertence à linha acionada: vale o identificador consultado.
  return record ? { ...record, id } : null;
}
